import { WizardStepChangeEventHandler } from 'components/types'
import { ChangeEvent, useEffect, useState } from 'react'
import { CustomColorsRef, FieldChangeEventAnswer, IconSizes } from 'types/general'

import IconRender from '../IconRender'
import FieldErrorMessage from './FieldErrorMessage'

interface Option {
  label: string
  value: string
  icon?: string
  checked?: boolean
}

interface MultiSelectProps {
  name: string
  label: string
  options: Option[]
  value: string
  required?: boolean
  onChange: (
    value:
      | WizardStepChangeEventHandler
      | ChangeEvent<HTMLInputElement>
      | FieldChangeEventAnswer
  ) => void
  setIsValid: (isValid: boolean) => void
  errorMessage?: string
}

const MultiSelect = ({
  name,
  label,
  options,
  value,
  required,
  onChange,
  setIsValid,
  errorMessage,
}: MultiSelectProps) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([])

  useEffect(() => {
    const initialValues = value?.split(',').filter(Boolean) || []
    setSelectedOptions(initialValues)
    setIsValid(required ? initialValues.length > 0 : true)
  }, [value, required, setIsValid])

  const handleChange = (optionValue: string) => {
    const updatedSelection = selectedOptions.includes(optionValue)
      ? selectedOptions.filter(val => val !== optionValue)
      : [...selectedOptions, optionValue]

    setSelectedOptions(updatedSelection)

    const updatedValue = updatedSelection.join(',')
    onChange({ name, value: updatedValue })

    setIsValid(required ? updatedSelection.length > 0 : true)
  }

  const hasError = Boolean(errorMessage)

  return (
    <div>
      <label
        htmlFor={name}
        className="block text-imovSecondaryDark font-bold mb-2 p-1 px-2"
      >
        {label}
      </label>
      <FieldErrorMessage
        hasError={!hasError}
        message={errorMessage}
        className="ml-2"
      />
      <div className="grid grid-cols-2 gap-1 p-4">
        {options.map(option => (
          <button
            key={option.value}
            onClick={() => handleChange(option.value)}
            className={`flex items-center space-x-2 p-2 rounded cursor-pointer transition-all duration-200 w-full text-left ${
              selectedOptions.includes(option.value)
                ? 'border-2 border-imovPrimary bg-imovPrimary bg-opacity-5'
                : 'border border-gray-300 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center space-x-2 flex-1">
              {option.icon && (
                <IconRender
                  icon={option.icon}
                  size={IconSizes.SM}
                  color={
                    selectedOptions.includes(option.value)
                      ? CustomColorsRef.Primary
                      : CustomColorsRef.Gray
                  }
                />
              )}
              <span
                className={`text-sm ${
                  selectedOptions.includes(option.value)
                    ? 'text-imovPrimary font-medium'
                    : 'text-gray-700'
                }`}
              >
                {option.label}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

export default MultiSelect
